import { InfoOutlined } from 'components/icons'
import { useEditMode } from 'purchasers/editMode'
import { path } from 'ramda'
import React from 'react'
import { SizableText, XStack, YStack } from 'tamagui'
import Checkbox from './checkbox'
import { Label } from './label'

interface SingleSelectCheckboxGroupProps {
    options: readonly string[] | string[] // Array of checkbox options
    value?: string | null // Selected value (single selection)
    onChange?: (value: string) => void // Callback with selected value
    label?: string // Field label
    disabled?: boolean // Disable all checkboxes
    error?: boolean | any // Error state
    layout?: 'row' | 'column' // Stack direction (default: row)
    gap?: string // Tamagui spacing token (default: '$4')
}

const SingleSelectCheckboxGroup = (props: SingleSelectCheckboxGroupProps) => {
    const { editMode } = useEditMode()
    const { options, value, onChange, label, disabled, layout, gap } = props

    // Read-only mode: display selected value as text
    if (!editMode) {
        return (
            <YStack gap="$2">
                {label && <Label>{label}</Label>}
                <SizableText>{value ?? '\u00A0'}</SizableText>
            </YStack>
        )
    }

    // Edit mode: render checkboxes with single-select behavior (radio-like)
    return (
        <YStack gap="$2">
            {label && <Label>{label}</Label>}
            <XStack
                gap={gap ?? '$4'}
                flexWrap="wrap"
                flexDirection={layout === 'column' ? 'column' : 'row'}
            >
                {options.map(option => {
                    const isChecked = value === option
                    return (
                        <Checkbox
                            key={option}
                            label={option}
                            checked={isChecked}
                            disabled={disabled}
                            onChange={() => onChange?.(option)}
                        />
                    )
                })}
            </XStack>
        </YStack>
    )
}

export default SingleSelectCheckboxGroup

interface SlotSingleSelectCheckboxGroupProps
    extends Omit<SingleSelectCheckboxGroupProps, 'error' | 'value' | 'onChange'> {
    field: any
    formState: any
    formControlProps?: any
    helperText?: string
}

export const SlotSingleSelectCheckboxGroup = ({
    field,
    formState,
    helperText,
    formControlProps,
    ...props
}: SlotSingleSelectCheckboxGroupProps) => {
    const { errors } = formState
    const error = path(field.name.split('.'), errors) as any

    return (
        <YStack gap="$2" {...formControlProps}>
            {helperText && !error?.message && (
                <SizableText size="$2" color="$gray10">
                    {helperText}
                </SizableText>
            )}
            <SingleSelectCheckboxGroup
                {...props}
                value={field.value}
                onChange={value => field.onChange(value)}
                error={error}
            />
            {error?.message && (
                <XStack alignItems="center" gap="$1">
                    <InfoOutlined size={14} />
                    <SizableText size="$2" color="$red10">
                        {error.message}
                    </SizableText>
                </XStack>
            )}
        </YStack>
    )
}
